// src/components/Report/shared/chartConfig.js
//
// Chart.js option builders shared by every bar chart across the report modules.
// Keeping these in one place means tooltip styling / grid styling only needs
// to change in one file to update every chart everywhere.

const TOOLTIP_BASE = {
  backgroundColor: "#1e293b",
  titleColor: "#f8fafc",
  bodyColor: "#cbd5e1",
  borderColor: "rgba(255,255,255,0.1)",
  borderWidth: 1,
  padding: 12,
  cornerRadius: 10,
};

/**
 * Vertical bar chart options — used for Subject Scores, Attempts,
 * Time Spent, Avg Marks, Score Distribution, etc.
 *
 * @param {string}  yLabel     suffix appended to y-axis ticks, e.g. "%" or "m"
 * @param {number}  yMax       fixed max for y-axis (omit for auto-scale)
 * @param {Function} labelFn   tooltip label callback (ctx) => string | string[]
 * @param {Function} clickFn   chart onClick handler (event, elements) => void
 * @param {number}  maxRotation x-axis label rotation (default 30)
 */
export function mkOpts({ yLabel = "", yMax, labelFn, clickFn, maxRotation = 30 } = {}) {
  return {
    responsive: true,
    maintainAspectRatio: false,
    animation: { duration: 500, easing: "easeOutQuart" },
    plugins: {
      legend: { display: false },
      tooltip: {
        ...TOOLTIP_BASE,
        callbacks: { label: labelFn || ((ctx) => ` ${ctx.parsed.y}`) },
      },
    },
    scales: {
      x: {
        grid: { display: false },
        ticks: { color: "#94a3b8", font: { size: 11, family: "'DM Sans',sans-serif" }, maxRotation },
        border: { display: false },
      },
      y: {
        min: 0,
        max: yMax,
        grid: { color: "rgba(0,0,0,0.05)" },
        ticks: { color: "#94a3b8", font: { size: 10 }, callback: (v) => (yLabel ? `${v}${yLabel}` : v) },
        border: { display: false },
      },
    },
    onClick: clickFn,
  };
}

/**
 * Horizontal bar chart options — used for student subject breakdown,
 * chapter-wise marks, etc.
 *
 * @param {number}  xMax    fixed max for x-axis (default 100, for percentages)
 * @param {Function} labelFn tooltip label callback (ctx) => string | string[]
 * @param {Function} clickFn chart onClick handler (event, elements) => void
 */
export function mkHorizOpts({ xMax = 100, labelFn, clickFn } = {}) {
  return {
    indexAxis: "y",
    responsive: true,
    maintainAspectRatio: false,
    animation: { duration: 500, easing: "easeOutQuart" },
    plugins: {
      legend: { display: false },
      tooltip: {
        ...TOOLTIP_BASE,
        callbacks: { label: labelFn || ((ctx) => ` ${ctx.parsed.x}%`) },
      },
    },
    scales: {
      x: {
        min: 0,
        max: xMax,
        grid: { color: "rgba(0,0,0,0.05)" },
        ticks: { color: "#94a3b8", font: { size: 10 }, callback: (v) => `${v}%` },
        border: { display: false },
      },
      y: {
        grid: { display: false },
        ticks: { color: "#475569", font: { size: 11, family: "'DM Sans',sans-serif" } },
        border: { display: false },
      },
    },
    onClick: clickFn,
  };
}